// SDO interface for timeline marks — mirrors desktop TimeMark
// Source: D:/Code/TR3AD/v4/Java/Desktop/.../api/timeline/TimeMark
//
// A TimeMark records a chronological event tied to an Intel or Resource profile.
// The `precision` controls how the time is displayed and which date inputs are
// visible in the editor (year-only vs year+month vs full datetime, etc.).

export type TimeMarkPrecision = "YR" | "YR_MON" | "DAY" | "DAY_HR" | "DAY_HM" | "DAY_HMS";

// Card type controls which visual card renderer is used in the timeline viewer.
// "text" is the default. New types (image, url, etc.) get their own card component
// in ux/network/timeline/cards/.
export type TimeMarkCardType = "text" | "image" | "url";

export interface TimeMark {
    uid: string;
    time: number;              // epoch milliseconds; 0 = unset
    precision: TimeMarkPrecision;
    title: string;
    details: string;
    cardType?: TimeMarkCardType;  // defaults to "text" when absent
}

// Numeric codes mirror desktop TimeMarkPrecision.getCode().
// Used for the visibility logic in the editor (showYear: code <= 2, etc.)
export const PRECISION_CODE: Record<TimeMarkPrecision, number> = {
    YR: 1,
    YR_MON: 2,
    DAY: 3,
    DAY_HR: 4,
    DAY_HM: 5,
    DAY_HMS: 6,
};

export const PRECISION_OPTIONS: { value: TimeMarkPrecision; label: string }[] = [
    {value: "YR", label: "Year"},
    {value: "YR_MON", label: "Year + Month"},
    {value: "DAY", label: "Day"},
    {value: "DAY_HR", label: "Day + Hour"},
    {value: "DAY_HM", label: "Day + Minute"},
    {value: "DAY_HMS", label: "Day + Second"},
];

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

function format12Hour(hour24: number): { hour: number; meridiem: string } {
    const meridiem = hour24 >= 12 ? "PM" : "AM";
    let h = hour24 % 12;
    if (h === 0) h = 12;
    return {hour: h, meridiem};
}

// Format a TimeMark's time according to its precision.
// Mirrors desktop TimeMarkCard.formatDateTime() patterns.
export function formatTimeMark(mark: TimeMark): string {
    if (!mark.time || mark.time <= 0) return "";
    const d = new Date(mark.time);
    const month = MONTH_SHORT[d.getMonth()];
    const day = pad2(d.getDate());
    const year = d.getFullYear();

    switch (mark.precision) {
        case "YR":
            return String(year);
        case "YR_MON":
            return `${month} ${year}`;
        case "DAY":
            return `${month} ${day}, ${year}`;
        case "DAY_HR": {
            const {hour, meridiem} = format12Hour(d.getHours());
            return `${month} ${day}, ${year} ${pad2(hour)} ${meridiem}`;
        }
        case "DAY_HM": {
            const {hour, meridiem} = format12Hour(d.getHours());
            return `${month} ${day}, ${year} ${pad2(hour)}:${pad2(d.getMinutes())} ${meridiem}`;
        }
        case "DAY_HMS":
        default: {
            const {hour, meridiem} = format12Hour(d.getHours());
            return `${month} ${day}, ${year} ${pad2(hour)}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())} ${meridiem}`;
        }
    }
}
